import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, Filter, Users, Briefcase, Clock, X } from 'lucide-react';
import { useAuth } from '../App';
import CollabRequestForm from '../components/CollabRequestForm';

const BACKEND_URL = '';  // Use relative URLs

const CollabBoard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    looking_for: '',
    genre: '',
    compensation: '',
    status: 'Open',
    sort: 'newest'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const LOOKING_FOR_OPTIONS = [
    'Artist', 'Writer', 'Colorist', 'Letterer', 
    'Voice Actor', 'Animator', 'Music Producer', 'Editor', 'Other'
  ];

  const GENRE_OPTIONS = [
    'Action', 'Fantasy', 'Sci-Fi', 'Romance', 'Comedy', 
    'Horror', 'Slice of Life', 'Mystery', 'Drama', 'Other'
  ];

  const COMPENSATION_OPTIONS = ['Paid', 'Revenue Share', 'Unpaid', 'Negotiable'];
  const STATUS_OPTIONS = ['Open', 'In Progress', 'Closed', 'all'];

  useEffect(() => {
    loadRequests(true);
  }, [filters, searchQuery]);

  const loadRequests = async (reset = false) => {
    try {
      const currentPage = reset ? 0 : page;
      const token = localStorage.getItem('token');
      
      const params = {
        skip: currentPage * 20,
        limit: 20,
        ...filters,
        search: searchQuery || undefined
      };

      const response = await axios.get('/api/collab-requests', {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (reset) {
        setRequests(response.data.requests);
        setPage(0);
      } else {
        setRequests(prev => [...prev, ...response.data.requests]);
      }

      setTotal(response.data.total);
      setHasMore(response.data.requests.length === 20);
      setLoading(false);
    } catch (error) {
      console.error('Error loading collab requests:', error);
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      looking_for: '',
      genre: '',
      compensation: '',
      status: 'Open',
      sort: 'newest'
    });
    setSearchQuery('');
  };

  const hasActiveFilters = () => {
    return searchQuery || 
           filters.looking_for || 
           filters.genre || 
           filters.compensation || 
           filters.status !== 'Open';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Open': return 'bg-green-500';
      case 'In Progress': return 'bg-yellow-500';
      case 'Closed': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const handleRequestCreated = () => {
    setShowForm(false);
    loadRequests(true);
  };

  return (
    <div className="min-h-screen bg-black pt-16 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Collaboration Board
              </h1>
              <p className="text-gray-400">
                Find creative partners for your projects
              </p>
            </div>
            
            {user && (user.role === 'artist' || user.role === 'author') && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-all"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">Create Request</span>
              </button>
            )}
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-900 text-white pl-12 pr-4 py-3 rounded-lg border border-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Filter Toggle & Active Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-800 hover:bg-gray-800 transition-colors"
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              {hasActiveFilters() && (
                <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  !
                </span>
              )}
            </button>

            {hasActiveFilters() && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
                <span className="text-sm">Clear Filters</span>
              </button>
            )}

            <div className="text-sm text-gray-500">
              {total} {total === 1 ? 'request' : 'requests'} found
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                
                {/* Looking For */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Looking For
                  </label>
                  <select
                    value={filters.looking_for}
                    onChange={(e) => handleFilterChange('looking_for', e.target.value)}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">All Roles</option>
                    {LOOKING_FOR_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Genre */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Genre
                  </label>
                  <select
                    value={filters.genre}
                    onChange={(e) => handleFilterChange('genre', e.target.value)}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">All Genres</option>
                    {GENRE_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Compensation */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Compensation
                  </label>
                  <select
                    value={filters.compensation}
                    onChange={(e) => handleFilterChange('compensation', e.target.value)}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">All Types</option>
                    {COMPENSATION_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option === 'all' ? 'All Statuses' : option}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sort */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Sort By
                  </label>
                  <select
                    value={filters.sort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="newest">Newest First</option>
                    <option value="most_active">Most Active</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Requests Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
            <p className="text-gray-400 mt-4">Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              No collaboration requests found
            </h3>
            <p className="text-gray-400 mb-6">
              {hasActiveFilters() 
                ? 'Try adjusting your filters or search query'
                : 'Be the first to post a collaboration request!'}
            </p>
            {user && (user.role === 'artist' || user.role === 'author') && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-3 rounded-lg font-semibold"
              >
                Create Your First Request
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map(request => (
              <div
                key={request.id}
                onClick={() => navigate(`/collab/${request.id}`)}
                className="bg-gray-900 border border-gray-800 rounded-lg p-6 hover:border-orange-500 transition-all cursor-pointer"
              >
                {/* Creator Info */}
                <div className="flex items-center space-x-3 mb-4">
                  {request.creator_profile_image ? (
                    <img
                      src={request.creator_profile_image}
                      alt={request.creator_username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {request.creator_username.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">
                      {request.creator_username}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`${getStatusColor(request.status)} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                    {request.status}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">
                  {request.title}
                </h3>

                {/* Description Preview */}
                <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                  {request.description}
                </p>

                {/* Tags */}
                <div className="space-y-2 mb-4">
                  {/* Looking For Tags */}
                  <div className="flex flex-wrap gap-2">
                    {request.looking_for.slice(0, 3).map((role, idx) => (
                      <span
                        key={idx}
                        className="bg-orange-500/20 text-orange-400 text-xs font-medium px-2 py-1 rounded-full flex items-center space-x-1"
                      >
                        <Users className="w-3 h-3" />
                        <span>{role}</span>
                      </span>
                    ))}
                    {request.looking_for.length > 3 && (
                      <span className="bg-gray-800 text-gray-400 text-xs font-medium px-2 py-1 rounded-full">
                        +{request.looking_for.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Genre Tags */}
                  <div className="flex flex-wrap gap-2">
                    {request.genre.slice(0, 2).map((g, idx) => (
                      <span
                        key={idx}
                        className="bg-purple-500/20 text-purple-400 text-xs font-medium px-2 py-1 rounded-full"
                      >
                        {g}
                      </span>
                    ))}
                    {request.genre.length > 2 && (
                      <span className="bg-gray-800 text-gray-400 text-xs font-medium px-2 py-1 rounded-full">
                        +{request.genre.length - 2}
                      </span>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                  <div className="flex items-center space-x-1 text-sm text-gray-400">
                    <Briefcase className="w-4 h-4" />
                    <span>{request.compensation}</span>
                  </div>
                  {request.timeline && (
                    <div className="flex items-center space-x-1 text-sm text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span className="truncate">{request.timeline}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More */}
        {hasMore && !loading && requests.length > 0 && (
          <div className="text-center mt-8">
            <button
              onClick={() => {
                setPage(prev => prev + 1);
                loadRequests(false);
              }}
              className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-lg border border-gray-800 transition-colors"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {/* Create Request Modal */}
      {showForm && (
        <CollabRequestForm
          onClose={() => setShowForm(false)}
          onSuccess={handleRequestCreated}
        />
      )}
    </div>
  );
};

export default CollabBoard;
